// Mirrors the snippet category keys defined on the backend at
// services/dieter-server/src/schema/legalos/snippets/constants.ts.
// Keep this list in sync.

export const SNIPPET_CATEGORY_KEYS = [
  'ANALYSIS_AND_TRACKING_SERVICE',
  'AUDIO_VIDEO_CONFERENCES',
  'API_PROVIDER',
  'CHAT',
  'CHATBOT',
  'CLOUDBACKUP',
  'COMMUNITY_PLATFORM',
  'COOKIE_CONSENT_TOOL',
  'CONTACT_FORM',
  'CRM',
  'HOSTER',
  'KI_TOOL',
  'MAILING_SERVICE',
  'MARKET_PLACE',
  'NEWSLETTER_SERVICE',
  'ORDER_PROCEDURE_SERVICE',
  'PAYMENT_SERVICE',
  'PHONE_SERVICE',
  'QUESTIONNAIRES_FORMS',
  'SCHEDULE_APPOINTMENT',
  'SOCIAL_PROFILE',
  'SOCIAL_LOGIN',
  'SOCIAL_MEDIA_SERVICE',
  'SOCIAL_MEDIA',
  'THIRD_PARTY_CONTENT',
  'TICKET_SALES',
  'AFFILIATE',
  'WEBSITE_BUILDING_SYSTEM',
] as const;

export type SnippetCategoryKey = (typeof SNIPPET_CATEGORY_KEYS)[number];

/**
 * Stored in PersistentState when the user has explicitly confirmed an empty selection for a
 * category — distinguishes "no services here" from "never answered". Mirrors the backend.
 */
export const NO_SELECTION_SENTINEL = '__NO_SELECTION__';

const SNIPPET_CATEGORY_SET = new Set<string>(SNIPPET_CATEGORY_KEYS);

export function isSnippetCategoryKey(value: string | null | undefined): value is SnippetCategoryKey {
  return !!value && SNIPPET_CATEGORY_SET.has(value);
}

/**
 * Human-readable German labels for snippet categories. Mirrors
 * services/dieter-server/src/schema/legalos/snippets/constants.ts. Used to
 * display friendly category names in the suggested-service panel and to
 * post-process LLM reasoning text that may leak enum keys.
 */
export const SNIPPET_CATEGORY_LABELS: Record<SnippetCategoryKey, string> = {
  ANALYSIS_AND_TRACKING_SERVICE: 'Analyse- und Trackingtools',
  AUDIO_VIDEO_CONFERENCES: 'Audio- und Videokonferenzen',
  API_PROVIDER: 'API-Anbieter',
  CHAT: 'Chat',
  CHATBOT: 'Chatbot',
  CLOUDBACKUP: 'Backups',
  COMMUNITY_PLATFORM: 'Community-Plattform',
  COOKIE_CONSENT_TOOL: 'Cookie-Consent-Tool',
  CONTACT_FORM: 'Kontaktaufnahme',
  CRM: 'CRM-Systeme',
  HOSTER: 'Hoster',
  KI_TOOL: 'KI-Dienste',
  MAILING_SERVICE: 'Mailingdienst',
  MARKET_PLACE: 'Marktplatz',
  NEWSLETTER_SERVICE: 'Newsletter',
  ORDER_PROCEDURE_SERVICE: 'Bestellprozess',
  PAYMENT_SERVICE: 'Zahlungsanbieter',
  PHONE_SERVICE: 'Telefonie',
  QUESTIONNAIRES_FORMS: 'Fragebögen/Formulare',
  SCHEDULE_APPOINTMENT: 'Terminbuchung',
  SOCIAL_PROFILE: 'Social-Media-Profile',
  SOCIAL_LOGIN: 'Social-Logins',
  SOCIAL_MEDIA_SERVICE: 'Social-Media-Dienste',
  SOCIAL_MEDIA: 'Social Media',
  THIRD_PARTY_CONTENT: 'Drittinhalte/Plugins',
  TICKET_SALES: 'Ticketverkauf',
  AFFILIATE: 'Affiliate-Marketing',
  WEBSITE_BUILDING_SYSTEM: 'Website Baukasten System/Hosting',
};

export function snippetCategoryLabel(key: string | null | undefined): string {
  if (!key) return '';
  if (isSnippetCategoryKey(key)) return SNIPPET_CATEGORY_LABELS[key];
  return key;
}

/**
 * Replaces any SNIPPET_CATEGORY_KEY substring with its German label.
 * Used to clean up LLM-generated text that may still contain raw enum keys.
 */
export function humanizeCategoryKeysInText(text: string): string {
  let out = text;
  for (const key of SNIPPET_CATEGORY_KEYS) {
    if (out.includes(key)) {
      out = out.split(key).join(SNIPPET_CATEGORY_LABELS[key]);
    }
  }
  return out;
}

export const SERVICE_SELECTION_APP_ID = 'app-dse-privacy-policy-create';
